import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConnieProvider } from "@/context/ConnieContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import LandingSplitScreen from "@/components/LandingSplitScreen";
import AuthCallback from "@/pages/AuthCallback";
import { STATE_SLUGS } from "@/lib/verticals";
import { WelcomeModal } from "@/components/WelcomeModal";
import { LocationPrompt } from "@/components/LocationPrompt";
import PresenceTracker from "@/components/PresenceTracker";

// Lazy-loaded routes: keep the initial JS bundle small (homepage shell only).
// Heavy deps (leaflet, recharts, qrcode, embla) now ship in per-route chunks.
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const DnaShared = lazy(() => import("@/pages/DnaShared"));
const Talent = lazy(() => import("@/pages/Talent"));
const TalentMapPage = lazy(() => import("@/pages/TalentMapPage"));
const Opportunities = lazy(() => import("@/pages/Opportunities"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const Employers = lazy(() => import("@/pages/Employers"));
const CreateProfile = lazy(() => import("@/pages/CreateProfile"));
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const SignupBusiness = lazy(() => import("@/pages/SignupBusiness"));
const SignupSalesman = lazy(() => import("@/pages/SignupSalesman"));
const Join = lazy(() => import("@/pages/Join"));
const MeetConnie = lazy(() => import("@/pages/MeetConnie"));
const Admin = lazy(() => import("@/pages/Admin"));
const AndrewBrain = lazy(() => import("@/pages/AndrewBrain"));
const AdminBusinessAccount = lazy(() => import("@/pages/AdminBusinessAccount"));
const AdminSalespersonAccount = lazy(() => import("@/pages/AdminSalespersonAccount"));
const AdminAmbassadorAccount = lazy(() => import("@/pages/AdminAmbassadorAccount"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PricingPreview = lazy(() => import("@/pages/PricingPreview"));
const ClaimVerify = lazy(() => import("@/pages/ClaimVerify"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Messages = lazy(() => import("@/pages/Messages"));
const Settings = lazy(() => import("@/pages/Settings"));
const Saved = lazy(() => import("@/pages/Saved"));
const Applicants = lazy(() => import("@/pages/Applicants"));
const MyApplications = lazy(() => import("@/pages/MyApplications"));
const PostJob = lazy(() => import("@/pages/PostJob"));
const Forms = lazy(() => import("@/pages/Forms"));
const About = lazy(() => import("@/pages/About"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const MagicBall = lazy(() => import("@/pages/MagicBall"));
const SuggestionBox = lazy(() => import("@/pages/SuggestionBox"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const SalesJobsLanding = lazy(() => import("@/pages/SalesJobsLanding"));
const StateLanding = lazy(() => import("@/pages/StateLanding"));
const VerticalCityLanding = lazy(() => import("@/pages/VerticalCityLanding"));
const VerticalIndex = lazy(() => import("@/pages/VerticalIndex"));
const NearMeRedirect = lazy(() => import("@/pages/NearMeRedirect"));
const HireOverview = lazy(() => import("@/pages/HireOverview"));
const HireCompare = lazy(() => import("@/pages/HireCompare"));
const HireLanding = lazy(() => import("@/pages/HireLanding"));
const HireState = lazy(() => import("@/pages/HireState"));
const HireVerticalHub = lazy(() => import("@/pages/HireVerticalHub"));
const HireVerticalCity = lazy(() => import("@/pages/HireVerticalCity"));
const CloserQuiz = lazy(() => import("@/pages/CloserQuiz"));
const CoreAssessment = lazy(() => import("@/pages/CoreAssessment"));
const CoreIntro = lazy(() => import("@/pages/CoreIntro"));
const HireIntro = lazy(() => import("@/pages/HireIntro"));
const CoreShare = lazy(() => import("@/pages/CoreShare"));
const SalesTypes = lazy(() => import("@/pages/SalesTypes"));
const CareerHoroscope = lazy(() => import("@/pages/CareerHoroscope"));
const HoroscopeSign = lazy(() => import("@/pages/HoroscopeSign"));
const NewToSales = lazy(() => import("@/pages/NewToSales"));
const ClosingProcess = lazy(() => import("@/pages/ClosingProcess"));
const CloserBook = lazy(() => import("@/pages/CloserBook"));
const CloserPlay = lazy(() => import("@/pages/CloserPlay"));
const SalesTraining = lazy(() => import("@/pages/SalesTraining"));
const HowToGetIntoSales = lazy(() => import("@/pages/HowToGetIntoSales"));
const AdLanding = lazy(() => import("@/pages/AdLanding"));
const HireAdLanding = lazy(() => import("@/pages/HireAdLanding"));
const FormFill = lazy(() => import("@/pages/FormFill"));
const CloserResult = lazy(() => import("@/pages/CloserResult"));
const HiringBadge = lazy(() => import("@/pages/HiringBadge"));
const IncomeCalculator = lazy(() => import("@/pages/IncomeCalculator"));
const JobDescriptionGenerator = lazy(() => import("@/pages/JobDescriptionGenerator"));
const Guides = lazy(() => import("@/pages/Guides"));
const Guide = lazy(() => import("@/pages/Guide"));
const BreakIntoSales = lazy(() => import("@/pages/BreakIntoSales"));
const BreakInDetail = lazy(() => import("@/pages/BreakInDetail"));
const WarRoom = lazy(() => import("@/pages/WarRoom"));
const FounderPage = lazy(() => import("@/pages/FounderPage"));
const TenX = lazy(() => import("@/pages/TenX"));

// City slugs end in a 2-letter state code (e.g. "austin-tx"); state hubs are known
// state slugs ("texas"); everything else is a TRADE vertical hub (e.g. "hvac",
// "water-heater"), including trades the owner adds later in Control Center.
function SalesJobsRoute() {
  const { slug } = useParams();
  if (/-[a-z]{2}$/.test(slug || "")) return <SalesJobsLanding />;
  if (STATE_SLUGS.has(slug)) return <StateLanding />;
  return <VerticalIndex />;
}

// Employer side: city (-XX) -> HireLanding; state slug -> HireState; else trade vertical.
function HireRoute() {
  const { slug } = useParams();
  if (/-[a-z]{2}$/.test(slug || "")) return <HireLanding />;
  if (STATE_SLUGS.has(slug)) return <HireState />;
  return <HireVerticalHub />;
}

function HomeGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="bg-[#0F172A] min-h-screen" />;
  if (!user) return <LandingSplitScreen />;
  const hub = user.role === "business" ? "/talent" : user.role === "salesman" ? "/jobs" : "/admin";
  return <Navigate to={hub} replace />;
}

// SEO/geo + growth-tool pages: crawlable for logged-out visitors (and prerendered for
// bots), but hidden from signed-in users, send them back into the app.
function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="bg-[#0F172A] min-h-screen" />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

const RouteFallback = () => <div className="bg-[#0F172A] min-h-screen" />;

function AppRouter() {
  const location = useLocation();
  React.useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const toTop = () => window.scrollTo(0, 0);
    toTop();
    const raf = requestAnimationFrame(toTop); // run again after the new page paints
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HomeGate />} />
          <Route path="/dna/:id" element={<DnaShared />} />
          <Route path="/talent" element={<Talent />} />
          <Route path="/talent-map" element={<TalentMapPage />} />
          <Route path="/opportunities" element={<Opportunities />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/employers" element={<Employers />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/p/:slug" element={<PublicProfile />} />
          <Route path="/connie-connection" element={<Blog />} />
          <Route path="/connie-connection/crystal-ball" element={<MagicBall />} />
          <Route path="/connie-connection/magic-ball" element={<Navigate to="/connie-connection/crystal-ball" replace />} />
          <Route path="/connie-connection/suggestion-box" element={<SuggestionBox />} />
          <Route path="/connie-connection/:slug" element={<BlogPost />} />
          <Route path="/jobs/:id" element={<JobDetail />} />
          <Route path="/sales-jobs/near-me" element={<GuestOnly><NearMeRedirect /></GuestOnly>} />
          <Route path="/sales-jobs/:slug" element={<GuestOnly><SalesJobsRoute /></GuestOnly>} />
          <Route path="/sales-jobs/:vertical/:slug" element={<GuestOnly><VerticalCityLanding /></GuestOnly>} />
          <Route path="/hire-sales-reps" element={<GuestOnly><HireOverview /></GuestOnly>} />
          <Route path="/hire-sales-reps/alternatives" element={<GuestOnly><HireCompare /></GuestOnly>} />
          <Route path="/hire-sales-reps/:slug" element={<GuestOnly><HireRoute /></GuestOnly>} />
          <Route path="/hire-sales-reps/:vertical/:slug" element={<GuestOnly><HireVerticalCity /></GuestOnly>} />
          <Route path="/closer-quiz" element={<GuestOnly><CloserQuiz /></GuestOnly>} />
          <Route path="/core-assessment" element={<CoreAssessment />} />
          <Route path="/core-intro" element={<CoreIntro />} />
          <Route path="/hire-intro" element={<HireIntro />} />
          <Route path="/core-assessment/:archetype" element={<CoreShare />} />
          <Route path="/sales-types" element={<SalesTypes />} />
          <Route path="/career-horoscope" element={<CareerHoroscope />} />
          <Route path="/career-horoscope/:sign" element={<HoroscopeSign />} />
          <Route path="/new-to-sales" element={<NewToSales />} />
          <Route path="/closing-process" element={<ClosingProcess />} />
          <Route path="/closing-process/book" element={<CloserBook />} />
          <Route path="/closing-process/play" element={<CloserPlay />} />
          <Route path="/sales-training" element={<SalesTraining />} />
          <Route path="/sales-training/:vertical" element={<SalesTraining />} />
          <Route path="/how-to-get-into-sales" element={<HowToGetIntoSales />} />
          <Route path="/forms/fill/:sid" element={<FormFill />} />
          <Route path="/closer-quiz/:type" element={<GuestOnly><CloserResult /></GuestOnly>} />
          <Route path="/hiring-badge" element={<GuestOnly><HiringBadge /></GuestOnly>} />
          <Route path="/sales-income-calculator" element={<GuestOnly><IncomeCalculator /></GuestOnly>} />
          <Route path="/job-description-generator" element={<GuestOnly><JobDescriptionGenerator /></GuestOnly>} />
          <Route path="/guides" element={<GuestOnly><Guides /></GuestOnly>} />
          <Route path="/guides/:slug" element={<GuestOnly><Guide /></GuestOnly>} />
          <Route path="/break-into-sales" element={<GuestOnly><BreakIntoSales /></GuestOnly>} />
          <Route path="/break-into-sales/:slug" element={<GuestOnly><BreakInDetail /></GuestOnly>} />
          <Route path="/create-profile" element={<CreateProfile />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/applicants" element={<Applicants />} />
          <Route path="/my-applications" element={<MyApplications />} />
          <Route path="/post-job" element={<PostJob />} />
          <Route path="/forms" element={<Forms />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/saved" element={<Saved />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/pricing-preview" element={<PricingPreview />} />
          <Route path="/claim/verify" element={<ClaimVerify />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/brain" element={<AndrewBrain />} />
          <Route path="/admin/business/:id" element={<AdminBusinessAccount />} />
          <Route path="/admin/salesperson/:id" element={<AdminSalespersonAccount />} />
          <Route path="/admin/ambassador/:id" element={<AdminAmbassadorAccount />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/join/:role" element={<Join />} />
        <Route path="/meet-connie/:role" element={<MeetConnie />} />
        <Route path="/signup/business" element={<SignupBusiness />} />
        <Route path="/signup/salesman" element={<SignupSalesman />} />
        <Route path="/go/hire/:trade" element={<HireAdLanding />} />
        <Route path="/go/:slug" element={<AdLanding />} />
        <Route path="/war-room" element={<WarRoom />} />
        <Route path="/10x" element={<TenX />} />
        <Route path="*" element={<FounderPage />} />
      </Routes>
    </Suspense>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="bottom-left" theme={theme} />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <ConnieProvider>
            <BrowserRouter>
              <AppRouter />
              <PresenceTracker />
              <WelcomeModal />
              <LocationPrompt />
              <ThemedToaster />
            </BrowserRouter>
          </ConnieProvider>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
